import { findFieldIndicator } from "@utils/fieldIndicator";
import { isReviewItemResolved } from "@utils/reportVisibility";
import { useEffect, useMemo, useRef, useState } from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import {
  Anchored,
  CARD_SPACING,
  ESTIMATED_CARD_HEIGHT,
  calculateAdjustedTop,
  isCardVisibleInRightPanel,
  orderItemsForNavigator,
} from "@components/QaReview/ReviewStack/positioning";
import { useAnchoredStack } from "@components/QaReview/ReviewStack/useAnchoredStack";
import { CommentComposer } from "@components/QaReview/components/CommentComposer";
import { CommentThread } from "@components/QaReview/components/CommentThread";
import { ActivatableCard, StackSlot } from "@components/QaReview/components/StackSlot";
import { ToFixCard } from "@components/QaReview/components/ToFixCard";
import { ToFixNote } from "@components/QaReview/components/ToFixNote";
import { useCanComment } from "@components/QaReview/hooks/useCanComment";
import { scrollToFieldIndicator, useGoToItem } from "@components/QaReview/hooks/useGoToItem";
import { useQaScanState } from "@components/QaReview/hooks/useQaScanState";
import { anchorOf, commentFieldTitle, domAnchorOf } from "@components/QaReview/hooks/useReviewAnchors";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { ReviewComposer, useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { useIsReviewItemListed } from "@components/QaReview/hooks/useReviewView";
import { Reviewer } from "@components/ReportReview/reviewers";
import { useAdminData } from "@providers/AdminDataProvider";

interface ReviewPanelProps {
  scrollParentRef?: React.RefObject<HTMLDivElement | null>;
  containerId?: string;
  /** Quality Review's checking state, pinned to the top of the card stack above the Team Review cards. */
  qaStatus?: React.ReactNode;
  /** The reviewers who sent the report back, each pinned as a to-fix card above the stack (author only). */
  toFixReviewers?: Reviewer[];
}

const QA_STATUS_SLOT_ID = "qa-scan-status";

const COMPOSER_ENTRY_ID = "peer-review-composer";

const UNPLACED_SLOT_ID = "unplaced-group";

type ItemStackEntry = Anchored & { kind: "item"; item: ReviewItemFieldsFragment };
type ComposerStackEntry = Anchored & { kind: "composer"; target: ReviewComposer };

// Whichever was chosen last is pinned: the new comment box when it opens, a card picked after it.
// Focusing a field without picking a card (active item null) leaves the box pinned.
const useComposerPinned = (composer: ReviewComposer | null, activeId: string | null): boolean => {
  const [pinned, setPinned] = useState({ composer, activeId, composerPinned: composer !== null });
  if (pinned.composer !== composer || pinned.activeId !== activeId) {
    const keepsPin = pinned.composerPinned && (activeId === pinned.activeId || activeId === null);
    setPinned({ composer, activeId, composerPinned: composer !== null && (composer !== pinned.composer || keepsPin) });
  }
  return pinned.composerPinned;
};

// A box whose field isn't on the page can't be drawn, but would stay pinned and throw the active card off
// its field. Set it aside, keeping the draft; only that box, in case another opened since it was measured.
const useSetAsideOffPageComposer = (
  offPageComposer: ReviewComposer | null,
  dispatch: ReturnType<typeof useReviewSession>["dispatch"],
) => {
  useEffect(() => {
    if (offPageComposer) dispatch({ type: "setAsideComposer", composer: offPageComposer });
  }, [offPageComposer, dispatch]);
};

interface TopSlotProps {
  toFixReviewers: Reviewer[];
  qaStatus: React.ReactNode;
  containerId: string;
}

// The author's to-fix cards share the top slot with the QA scan status.
const TopSlot: React.FC<TopSlotProps> = ({ toFixReviewers, qaStatus, containerId }) => (
  <div className="space-y-2">
    {toFixReviewers.map((reviewer) => (
      <ToFixCard key={reviewer.id} reviewer={reviewer} containerId={containerId}>
        <ToFixNote reviewer={reviewer} />
      </ToFixCard>
    ))}
    {qaStatus}
  </div>
);

const selectLabel = (item: ReviewItemFieldsFragment) => {
  const fieldTitle = commentFieldTitle(item);
  return fieldTitle ? `Select the card on "${fieldTitle}"` : "Select card";
};

interface UnplacedGroupProps {
  items: ReviewItemFieldsFragment[];
  repliesByParentId: Map<string, ReviewItemFieldsFragment[]>;
  activeId: string | null;
  onActivate: (item: ReviewItemFieldsFragment) => void;
  /** A click on the card's body, which also opens its replies. */
  onSelect: (item: ReviewItemFieldsFragment) => void;
}

// Cards whose field isn't on this page (a goal or target the page shows as read-only text). The review bar
// still counts and steps to them, and Complete waits on them, so they get a card here instead of none.
const UnplacedGroup: React.FC<UnplacedGroupProps> = ({ items, repliesByParentId, activeId, onActivate, onSelect }) => {
  const listRef = useRef<HTMLDivElement | null>(null);

  // These cards have no field to center, so stepping to one only brings its card into view.
  useEffect(() => {
    if (!activeId) return;
    const card = Array.from(listRef.current?.children ?? []).find(
      (child) => (child as HTMLElement).dataset.unplacedCard === activeId,
    );
    card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeId]);

  return (
    <section aria-labelledby="unplaced-cards-heading">
      <h3 id="unplaced-cards-heading" className="px-1 pb-2 text-xs font-medium text-text-tertiary">
        Not shown on this page
      </h3>
      <div ref={listRef} className="flex flex-col" style={{ gap: `${CARD_SPACING}px` }}>
        {items.map((item) => (
          <div
            key={item.id}
            data-unplaced-card={item.id}
            // Positions the card's go-to button, and scrolls clear of the fixed review bar.
            className="relative scroll-mb-[calc(var(--review-bar-offset,0px)+1rem)]"
          >
            <ActivatableCard label={selectLabel(item)} onClick={() => onSelect(item)} active={activeId === item.id}>
              <CommentThread
                root={item}
                replies={repliesByParentId.get(item.id) ?? []}
                isActive={activeId === item.id}
                onActivate={() => onActivate(item)}
              />
            </ActivatableCard>
          </div>
        ))}
      </div>
    </section>
  );
};

const ClearedState: React.FC<{ isFronteraInternal: boolean }> = ({ isFronteraInternal }) => (
  <div className="flex flex-col items-center justify-center gap-y-2 px-4 pt-20 text-center">
    <Paragraph displayType="loud" colorType="primary" size="small">
      Checkpoint cleared
    </Paragraph>
    {isFronteraInternal ? (
      <Paragraph displayType="normal" colorType="tertiary" size="extra-small">
        that&apos;s cash 💸
      </Paragraph>
    ) : (
      <Paragraph className="flex items-center gap-x-1" displayType="normal" colorType="tertiary" size="extra-small">
        Nothing left to review. Nice job!
      </Paragraph>
    )}
  </div>
);

export const ReviewPanel: React.FC<ReviewPanelProps> = ({
  scrollParentRef,
  containerId = "AssessmentReport",
  qaStatus = null,
  toFixReviewers = [],
}) => {
  const { roles } = useAdminData();
  const isFronteraInternal = roles.includes("frontera-internal");
  const canComment = useCanComment();
  const { resolvableQaItems, rootComments, repliesByParentId } = useReviewData();
  const goToItem = useGoToItem();
  const { state: session, dispatch } = useReviewSession();
  const { isChecking, isFailed: isQaFailed } = useQaScanState();

  const [scrollOffset, setScrollOffset] = useState(0);
  const hasToFix = toFixReviewers.length > 0;
  const hasQaStatus = hasToFix || !!qaStatus;

  const activeId = session.active?.itemId ?? null;
  const isListed = useIsReviewItemListed();
  const reviewItems = useMemo(() => [...resolvableQaItems, ...rootComments], [resolvableQaItems, rootComments]);
  // QA flags and peer threads the review bar's picked view lists.
  const listedItems = useMemo(() => reviewItems.filter(isListed), [reviewItems, isListed]);

  const composer = session.composer?.kind === "comment" ? session.composer : null;

  // Suggestions/comments only — the navigator's next/prev list is built from this, since a draft
  // composer isn't a persisted item to jump between.
  const itemEntries = useMemo<ItemStackEntry[]>(
    () =>
      listedItems.map((item) => ({
        id: item.id,
        anchorFieldId: domAnchorOf(item),
        rowFallbackId: item.__typename === "ReportSuggestionType" ? anchorOf(item)?.split("::")[0] ?? null : null,
        kind: "item" as const,
        item,
      })),
    [listedItems],
  );

  // The draft composer takes its place in the same anchored stack as everything else, so a
  // brand-new comment lands next to its field in field order and pushes/gets pushed by
  // neighboring cards instead of floating over the report as a separate overlay. It goes first
  // (the position sort is stable, so it leads its field's cards when their tops tie) and is the
  // pinned card while open, so it sits at its field's top in view instead of below its QA flags.
  const stackEntries = useMemo<(ItemStackEntry | ComposerStackEntry)[]>(
    () => [
      ...(composer
        ? [{ id: COMPOSER_ENTRY_ID, anchorFieldId: composer.fieldId, kind: "composer" as const, target: composer }]
        : []),
      ...itemEntries,
    ],
    [itemEntries, composer],
  );

  const composerPinned = useComposerPinned(composer, activeId);

  const { order, rawPositions, positions, heights, validIds, isMeasured, registerRef } = useAnchoredStack(
    stackEntries,
    {
      containerId,
      scrollRef: scrollParentRef,
      activeId: composerPinned ? COMPOSER_ENTRY_ID : activeId,
      topSlotId: hasQaStatus ? QA_STATUS_SLOT_ID : null,
    },
  );

  const isComposerOffPage = isMeasured && !validIds.has(COMPOSER_ENTRY_ID);
  useSetAsideOffPageComposer(isComposerOffPage ? composer : null, dispatch);

  useEffect(() => {
    const right = scrollParentRef?.current;
    if (!right) return;
    const onScroll = () => setScrollOffset(right.scrollTop || 0);
    const rafId = requestAnimationFrame(onScroll);
    right.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(rafId);
      right.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [scrollParentRef]);

  // Slots outside the right panel stay mounted (for measuring) but aren't painted.
  const isSlotVisible = (id: string) =>
    isCardVisibleInRightPanel(
      calculateAdjustedTop(positions[id] ?? 0, scrollOffset),
      heights[id] || ESTIMATED_CARD_HEIGHT,
      scrollParentRef?.current ?? null,
    );

  // After the walker/indicator activates a suggestion, scroll its field into view once the field is mounted. A
  // collapsed target row only mounts its field after requestItemExpand, and its card is already placed at the row,
  // so this waits for the field itself, re-checked whenever the stack re-measures.
  const activeFieldId = session.active?.fieldId ?? null;
  const activeFieldMounted =
    !!activeFieldId && !!activeId && validIds.has(activeId) && !!findFieldIndicator(activeFieldId);
  useEffect(() => {
    if (!activeFieldId || !activeFieldMounted) return;
    scrollToFieldIndicator(activeFieldId);
  }, [activeFieldId, activeFieldMounted]);

  // The review bar steps through every listed card, resolved ones included, in report order. It reads this
  // order from the session.
  const navigatorItems = useMemo(
    () => orderItemsForNavigator(itemEntries, rawPositions, validIds, containerId).map((entry) => entry.item),
    [itemEntries, rawPositions, validIds, containerId],
  );

  // Listed cards whose field isn't on the page, in the bar's order. Only cards the stack has measured can be
  // missing: one that just arrived waits for the next measurement instead of showing here for a frame and then
  // moving to its field. A list change re-measures for a frame; the cards already in the group stay mounted
  // through it, so a half-written reply in one survives.
  const [measured, setMeasured] = useState<{ entries: typeof stackEntries; ids: ReadonlySet<string> }>(() => ({
    entries: [],
    ids: new Set(),
  }));
  if (isMeasured && measured.entries !== stackEntries) {
    setMeasured({ entries: stackEntries, ids: new Set(stackEntries.map((entry) => entry.id)) });
  }
  const unplacedItems = useMemo(
    () => navigatorItems.filter((item) => measured.ids.has(item.id) && !validIds.has(item.id)),
    [navigatorItems, measured, validIds],
  );

  // Same as clicking a placed card: an open card's replies open too.
  const selectUnplaced = (item: ReviewItemFieldsFragment) => {
    if ((repliesByParentId.get(item.id)?.length ?? 0) > 0 && !isReviewItemResolved(item))
      dispatch({ type: "expandReplies", rootId: item.id });
    goToItem(item);
  };

  const placedBottom = useMemo(() => {
    let maxBottom = hasQaStatus ? heights[QA_STATUS_SLOT_ID] || ESTIMATED_CARD_HEIGHT : 0;
    order.forEach((entry) => {
      maxBottom = Math.max(maxBottom, (positions[entry.id] ?? 0) + (heights[entry.id] || ESTIMATED_CARD_HEIGHT));
    });
    return maxBottom;
  }, [order, positions, heights, hasQaStatus]);

  // The group follows the last placed card, so no placed card moves for it.
  const unplacedTop = placedBottom > 0 ? placedBottom + CARD_SPACING : 0;
  const totalHeight = useMemo(() => {
    const groupBottom =
      unplacedItems.length > 0
        ? unplacedTop + (heights[UNPLACED_SLOT_ID] || ESTIMATED_CARD_HEIGHT * unplacedItems.length)
        : 0;
    const maxBottom = Math.max(placedBottom, groupBottom);
    return maxBottom > 0 ? maxBottom + 20 : 0;
  }, [placedBottom, unplacedItems, unplacedTop, heights]);

  // Once measured, so the bar doesn't read the unplaced first render as an empty view.
  useEffect(() => {
    if (!isMeasured) return;
    dispatch({ type: "setNavigationOrder", itemIds: navigatorItems.map((item) => item.id) });
  }, [navigatorItems, isMeasured, dispatch]);

  // Nothing in the review is open (a clean report, or every item resolved). The bar reads "Nothing open"; a
  // narrower view that lists nothing stays blank, since the bar offers its way back. Not while Quality Review
  // checks or has failed: its flags aren't loaded, so an empty or all-resolved list doesn't clear the review.
  const isCleared =
    !isChecking && !isQaFailed && itemEntries.length === 0 && !composer && reviewItems.every(isReviewItemResolved);
  // With a to-fix card, the stack below stands in for the cleared message: the card, showing it all done,
  // stays pinned in its slot instead of remounting as the last item resolves.
  if (isCleared && !hasToFix) return <ClearedState isFronteraInternal={isFronteraInternal} />;

  return (
    <div
      className="relative w-full"
      style={{ minHeight: "100%", height: totalHeight > 0 ? `${totalHeight}px` : "100%" }}
    >
      {hasQaStatus && (
        <StackSlot top={calculateAdjustedTop(0, scrollOffset)} registerRef={(el) => registerRef(QA_STATUS_SLOT_ID, el)}>
          <TopSlot toFixReviewers={toFixReviewers} qaStatus={qaStatus} containerId={containerId} />
        </StackSlot>
      )}
      {order.map((entry) => {
        const baseTop = positions[entry.id] ?? 0;
        const adjustedTop = calculateAdjustedTop(baseTop, scrollOffset);

        if (entry.kind === "composer") {
          if (!entry.anchorFieldId || !canComment) return null;
          return (
            <StackSlot key={entry.id} top={adjustedTop} registerRef={(el) => registerRef(entry.id, el)}>
              <CommentComposer target={entry.target} />
            </StackSlot>
          );
        }

        const { item } = entry;
        const fieldTitle = commentFieldTitle(item);
        const replies = repliesByParentId.get(item.id) ?? [];
        // Clicking an open card also opens its replies, like the replies toggle; it never closes them.
        // A resolved card is a summary whose own button opens it.
        const activateCard = () => {
          if (replies.length > 0 && !isReviewItemResolved(item)) dispatch({ type: "expandReplies", rootId: item.id });
          goToItem(item);
        };
        return (
          <StackSlot
            key={item.id}
            top={adjustedTop}
            hidden={!isSlotVisible(item.id)}
            // Resolved cards render as short summaries.
            measureWhileHidden={isReviewItemResolved(item)}
            registerRef={(el) => registerRef(item.id, el)}
            active={activeId === item.id}
            activate={{
              label: fieldTitle ? `Go to "${fieldTitle}"` : "Go to field",
              onClick: activateCard,
            }}
          >
            <CommentThread
              root={item}
              replies={replies}
              isActive={activeId === item.id}
              onActivate={() => goToItem(item)}
            />
          </StackSlot>
        );
      })}
      {unplacedItems.length > 0 && (
        <StackSlot
          top={calculateAdjustedTop(unplacedTop, scrollOffset)}
          registerRef={(el) => registerRef(UNPLACED_SLOT_ID, el)}
        >
          <UnplacedGroup
            items={unplacedItems}
            repliesByParentId={repliesByParentId}
            activeId={activeId}
            onActivate={goToItem}
            onSelect={selectUnplaced}
          />
        </StackSlot>
      )}
    </div>
  );
};
